import { defineMessages, type IntlShape } from 'react-intl';

import { CurrencyType } from '~/eds/src/enums/CurrencyType';

export const DEFAULT_CURRENCY = CurrencyType.Usd;

const currencyMessages = defineMessages({
  [CurrencyType.Usd]: {
    id: 'Select.CurrencySelect.Usd',
    defaultMessage: 'United States Dollar',
    description: 'Currency label for USD',
  },
  [CurrencyType.Aud]: {
    id: 'Select.CurrencySelect.Aud',
    defaultMessage: 'Australian Dollar',
    description: 'Currency label for AUD',
  },
  [CurrencyType.Cad]: {
    id: 'Select.CurrencySelect.Cad',
    defaultMessage: 'Canadian Dollar',
    description: 'Currency label for CAD',
  },
  [CurrencyType.Chf]: {
    id: 'Select.CurrencySelect.Chf',
    defaultMessage: 'Swiss Franc',
    description: 'Currency label for CHF',
  },
  [CurrencyType.Cny]: {
    id: 'Select.CurrencySelect.Cny',
    defaultMessage: 'Chinese Yuan (Renminbi)',
    description: 'Currency label for CNY',
  },
  [CurrencyType.Dkk]: {
    id: 'Select.CurrencySelect.Dkk',
    defaultMessage: 'Danish Krone',
    description: 'Currency label for DKK',
  },
  [CurrencyType.Eur]: {
    id: 'Select.CurrencySelect.Eur',
    defaultMessage: 'European Euro',
    description: 'Currency label for EUR',
  },
  [CurrencyType.Gbp]: {
    id: 'Select.CurrencySelect.Gbp',
    defaultMessage: 'Great Britain (UK) Pound',
    description: 'Currency label for GBP',
  },
  [CurrencyType.Hkd]: {
    id: 'Select.CurrencySelect.Hkd',
    defaultMessage: 'Hong Kong Dollar',
    description: 'Currency label for HKD',
  },
  [CurrencyType.Ils]: {
    id: 'Select.CurrencySelect.Ils',
    defaultMessage: 'Israeli Shekel',
    description: 'Currency label for ILS',
  },
  [CurrencyType.Inr]: {
    id: 'Select.CurrencySelect.Inr',
    defaultMessage: 'Indian Rupee',
    description: 'Currency label for INR',
  },
  [CurrencyType.Jpy]: {
    id: 'Select.CurrencySelect.Jpy',
    defaultMessage: 'Japanese Yen',
    description: 'Currency label for JPY',
  },
  [CurrencyType.Krw]: {
    id: 'Select.CurrencySelect.Krw',
    defaultMessage: 'South Korean Won',
    description: 'Currency label for KRW',
  },
  [CurrencyType.Mxn]: {
    id: 'Select.CurrencySelect.Mxn',
    defaultMessage: 'Mexican Peso',
    description: 'Currency label for MXN',
  },
  [CurrencyType.Nok]: {
    id: 'Select.CurrencySelect.Nok',
    defaultMessage: 'Norwegian Krone',
    description: 'Currency label for NOK',
  },
  [CurrencyType.Nzd]: {
    id: 'Select.CurrencySelect.Nzd',
    defaultMessage: 'New Zealand Dollar',
    description: 'Currency label for NZD',
  },
  [CurrencyType.Rub]: {
    id: 'Select.CurrencySelect.Rub',
    defaultMessage: 'Russian Ruble',
    description: 'Currency label for RUB',
  },
  [CurrencyType.Sek]: {
    id: 'Select.CurrencySelect.Sek',
    defaultMessage: 'Swedish Krona',
    description: 'Currency label for SEK',
  },
  [CurrencyType.Sgd]: {
    id: 'Select.CurrencySelect.Sgd',
    defaultMessage: 'Singapore Dollar',
    description: 'Currency label for SGD',
  },
  [CurrencyType.Thb]: {
    id: 'Select.CurrencySelect.Thb',
    defaultMessage: 'Thailand Baht',
    description: 'Currency label for THB',
  },
  [CurrencyType.Vnd]: {
    id: 'Select.CurrencySelect.Vnd',
    defaultMessage: 'Vietnamese Dong',
    description: 'Currency label for VND',
  },
  [CurrencyType.Zar]: {
    id: 'Select.CurrencySelect.Zar',
    defaultMessage: 'South African Rand',
    description: 'Currency label for ZAR',
  },
});

// Reuses the same message ids as 1.0's `Select/subcomponents/useOptions` so
// translations carry over — this is the only consumer left importing the 1.0
// helper directly, so it's inlined here rather than reaching into a 1.0
// internal subcomponent from 2.0-only code.
//
// Explicit order (not `Object.values(CurrencyType)`, which is alphabetical by
// enum key): USD first as the default/most-common currency, then the rest
// alphabetically by display name — matches 1.0's `useOptions` ordering.
const CURRENCY_ORDER = [
  CurrencyType.Usd,
  CurrencyType.Aud,
  CurrencyType.Cad,
  CurrencyType.Chf,
  CurrencyType.Cny,
  CurrencyType.Dkk,
  CurrencyType.Eur,
  CurrencyType.Gbp,
  CurrencyType.Hkd,
  CurrencyType.Ils,
  CurrencyType.Inr,
  CurrencyType.Jpy,
  CurrencyType.Krw,
  CurrencyType.Mxn,
  CurrencyType.Nok,
  CurrencyType.Nzd,
  CurrencyType.Rub,
  CurrencyType.Sek,
  CurrencyType.Sgd,
  CurrencyType.Thb,
  CurrencyType.Vnd,
  CurrencyType.Zar,
] as const;

export function getCurrencyCodes(): string[] {
  return [...CURRENCY_ORDER];
}

export function getCurrencyOptions(intl: IntlShape) {
  return CURRENCY_ORDER.map((currency) => ({
    id: currency,
    value: currency,
    text: intl.formatMessage(currencyMessages[currency]),
  }));
}
